import React, { useState, useCallback } from 'react';
import './App.css';
import { InvoiceSettings, ExtraCostItem, ServiceLineItem, Invoice } from '../shared/types';
import { generateInvoicePDF } from './utils/pdfGenerator';
import { normalizeServiceLines, totalMinutes } from './utils/serviceLines';
import { saveSettings, loadSettings } from './utils/settings';
import { invoiceApi } from './api/invoice.api';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoginForm } from './components/auth/LoginForm';
import { RegisterForm } from './components/auth/RegisterForm';
import InvoiceList from './components/InvoiceList';
import TitleBar from './components/TitleBar';
import InvoicerDetails from './components/InvoicerDetails';
import ClientDetails from './components/ClientDetails';
import InvoicePeriod from './components/InvoicePeriod';
import ServiceLines from './components/ServiceLines';
import CostDetails from './components/CostDetails';
import Toast from './components/Toast';
import Notes from './components/Notes';

interface ToastState {
  message: string;
  type: 'success' | 'error';
  visible: boolean;
}

const App: React.FC = () => {
  const [currentInvoiceId, setCurrentInvoiceId] = useState<number | undefined>(undefined);
  const [invoiceName, setInvoiceName] = useState('');
  const [invoicerName, setInvoicerName] = useState('');
  const [invoicerAddress, setInvoicerAddress] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientEIN, setClientEIN] = useState('');
  const [timesheetUrl, setTimesheetUrl] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [serviceLines, setServiceLines] = useState<ServiceLineItem[]>([
    { description: '', hours: '', minutes: '' }
  ]);
  const [hourlyRate, setHourlyRate] = useState('');
  const [extraCosts, setExtraCosts] = useState<ExtraCostItem[]>([]);
  const [rsuPercentage, setRsuPercentage] = useState('');
  const [rsuBalanceBeforeTransfer, setRsuBalanceBeforeTransfer] = useState('');
  const [message, setMessage] = useState('');
  const [singleLineCostMode, setSingleLineCostMode] = useState(false);
  const [seasonalGraphic, setSeasonalGraphic] = useState<'winter' | 'nye' | null>(null);
  const [toast, setToast] = useState<ToastState>({
    message: '',
    type: 'success',
    visible: false
  });

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type, visible: true });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, visible: false }));
  };

  const handleInvoicerNameChange = useCallback((value: string) => {
    setInvoicerName(value);
  }, []);

  const handleInvoicerAddressChange = useCallback((value: string) => {
    setInvoicerAddress(value);
  }, []);

  const handleClientNameChange = useCallback((value: string) => {
    setClientName(value);
  }, []);

  const handleClientAddressChange = useCallback((value: string) => {
    setClientAddress(value);
  }, []);

  const handleClientEINChange = useCallback((value: string) => {
    setClientEIN(value);
  }, []);

  const handleTimesheetUrlChange = useCallback((value: string) => {
    setTimesheetUrl(value);
  }, []);

  const handleStartDateChange = useCallback((value: string) => {
    setStartDate(value);
  }, []);

  const handleEndDateChange = useCallback((value: string) => {
    setEndDate(value);
  }, []);

  const handleServiceLinesChange = useCallback((lines: ServiceLineItem[]) => {
    setServiceLines(lines);
  }, []);

  const handleHourlyRateChange = useCallback((value: string) => {
    setHourlyRate(value);
  }, []);

  const handleExtraCostsChange = useCallback((items: ExtraCostItem[]) => {
    setExtraCosts(items);
  }, []);

  const handleRsuPercentageChange = useCallback((value: string) => {
    setRsuPercentage(value);
  }, []);

  const handleRsuBalanceBeforeTransferChange = useCallback((value: string) => {
    setRsuBalanceBeforeTransfer(value);
  }, []);

  const handleMessageChange = useCallback((value: string) => {
    setMessage(value);
  }, []);

  const handleSeasonalGraphicChange = useCallback((value: 'winter' | 'nye' | null) => {
    setSeasonalGraphic(value);
  }, []);

  const getCurrentSettings = (): InvoiceSettings => {
    // Derive legacy single-service fields from serviceLines for backward compat
    const totalMins = totalMinutes(serviceLines);
    return {
      invoicerName,
      invoicerAddress,
      clientName,
      clientAddress,
      clientEIN,
      timesheetUrl,
      startDate,
      endDate,
      serviceLines,
      serviceDescription: serviceLines.map(l => l.description).filter(Boolean).join('; '),
      hours: String(Math.floor(totalMins / 60)),
      minutes: String(Math.round(totalMins % 60)),
      hourlyRate,
      extraCosts,
      rsuPercentage,
      rsuBalanceBeforeTransfer,
      message,
      singleLineCostMode,
      seasonalGraphic
    };
  };

  const handleSaveInvoice = async () => {
    try {
      const settings = getCurrentSettings();
      const autoName = `${settings.clientName || 'Untitled'} - ${settings.endDate || new Date().toLocaleDateString()}`;
      const invoiceData = {
        ...settings,
        name: invoiceName.trim() || autoName
      };

      if (currentInvoiceId) {
        // Update existing
        const updated = await invoiceApi.update(currentInvoiceId, invoiceData);
        showToast('Invoice updated successfully!');
      } else {
        // Create new
        const created = await invoiceApi.create(invoiceData);
        setCurrentInvoiceId(created.id);
        showToast('Invoice saved successfully!');
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      showToast('Failed to save invoice. Please try again.', 'error');
    }
  };

  const handleNewInvoice = useCallback(() => {
    setCurrentInvoiceId(undefined);
    setInvoiceName('');
    setInvoicerName('');
    setInvoicerAddress('');
    setClientName('');
    setClientAddress('');
    setClientEIN('');
    setTimesheetUrl('');
    setStartDate('');
    setEndDate('');
    setServiceLines([{ description: '', hours: '', minutes: '' }]);
    setHourlyRate('');
    setExtraCosts([]);
    setRsuPercentage('');
    setRsuBalanceBeforeTransfer('');
    setMessage('');
    setSingleLineCostMode(false);
    setSeasonalGraphic(null);
    showToast('Started new invoice');
  }, []);

  const handleLoadInvoice = useCallback((invoice: Invoice) => {
    setCurrentInvoiceId(invoice.id);
    setInvoiceName(invoice.name || '');
    setInvoicerName(invoice.invoicerName || '');
    setInvoicerAddress(invoice.invoicerAddress || '');
    setClientName(invoice.clientName || '');
    setClientAddress(invoice.clientAddress || '');
    setClientEIN(invoice.clientEIN || '');
    setTimesheetUrl(invoice.timesheetUrl || '');
    setStartDate(invoice.startDate || '');
    setEndDate(invoice.endDate || '');
    const loadedLines = normalizeServiceLines(invoice);
    setServiceLines(loadedLines.length > 0 ? loadedLines : [{ description: '', hours: '', minutes: '' }]);
    setHourlyRate(invoice.hourlyRate || '');
    setExtraCosts(Array.isArray(invoice.extraCosts) ? invoice.extraCosts : []);
    setRsuPercentage(invoice.rsuPercentage || '');
    setRsuBalanceBeforeTransfer(invoice.rsuBalanceBeforeTransfer || '');
    setMessage(invoice.message || '');
    setSingleLineCostMode(invoice.singleLineCostMode || false);
    setSeasonalGraphic(invoice.seasonalGraphic || null);
    showToast('Invoice loaded successfully!');
  }, []);

  // Legacy local storage handlers (optional, kept for transition if needed)
  const handleSaveSettings = useCallback(() => {
    saveSettings(getCurrentSettings());
    showToast('Settings saved to Local Storage');
  }, [invoicerName, invoicerAddress, clientName, clientAddress, clientEIN, timesheetUrl, startDate, endDate, serviceLines, hourlyRate, extraCosts, rsuPercentage, rsuBalanceBeforeTransfer, message, singleLineCostMode, seasonalGraphic]);

  const handleGeneratePDF = useCallback(() => {
    try {
      generateInvoicePDF(getCurrentSettings());
      showToast('PDF generated successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showToast('Failed to generate PDF. Please try again.', 'error');
    }
  }, [
    invoicerName, invoicerAddress, clientName, clientAddress, clientEIN,
    timesheetUrl, startDate, endDate, serviceLines, hourlyRate,
    extraCosts, rsuPercentage, rsuBalanceBeforeTransfer, message, singleLineCostMode, seasonalGraphic
  ]);

  return (
    <>
      <TitleBar />
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ margin: 0 }}>Invoice Generator</h1>
          <LogoutButton />
        </div>

        <div className="button-group" style={{ flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <InvoiceList onLoadInvoice={handleLoadInvoice} />
            <button onClick={handleNewInvoice} className="secondary-button" style={{ marginBottom: '10px' }}>New Invoice</button>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="invoice-name">Invoice Name</label>
            <input
              id="invoice-name"
              type="text"
              value={invoiceName}
              onChange={(e) => setInvoiceName(e.target.value)}
              placeholder="Auto-named from client & date if left blank"
            />
          </div>
          <button onClick={handleSaveInvoice} className="primary-button" style={{ marginTop: 0, backgroundColor: '#2196F3' }}>
            {currentInvoiceId ? 'Update Invoice' : 'Save Invoice'}
          </button>
        </div>

        <InvoicerDetails
          invoicerName={invoicerName}
          invoicerAddress={invoicerAddress}
          onNameChange={handleInvoicerNameChange}
          onAddressChange={handleInvoicerAddressChange}
        />

        <ClientDetails
          clientName={clientName}
          clientAddress={clientAddress}
          clientEIN={clientEIN}
          timesheetUrl={timesheetUrl}
          onNameChange={handleClientNameChange}
          onAddressChange={handleClientAddressChange}
          onEINChange={handleClientEINChange}
          onTimesheetUrlChange={handleTimesheetUrlChange}
        />

        <InvoicePeriod
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
        />

        <ServiceLines
          serviceLines={serviceLines}
          onServiceLinesChange={handleServiceLinesChange}
        />

        <CostDetails
          hourlyRate={hourlyRate}
          extraCosts={extraCosts}
          rsuPercentage={rsuPercentage}
          rsuBalanceBeforeTransfer={rsuBalanceBeforeTransfer}
          singleLineCostMode={singleLineCostMode}
          onHourlyRateChange={handleHourlyRateChange}
          onExtraCostsChange={handleExtraCostsChange}
          onRsuPercentageChange={handleRsuPercentageChange}
          onRsuBalanceBeforeTransferChange={handleRsuBalanceBeforeTransferChange}
          onSingleLineCostModeChange={setSingleLineCostMode}
        />

        <Notes
          message={message}
          seasonalGraphic={seasonalGraphic}
          onMessageChange={handleMessageChange}
          onSeasonalGraphicChange={handleSeasonalGraphicChange}
        />

        <button onClick={handleGeneratePDF} className="primary-button">Generate Invoice PDF</button>

        {toast.visible && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={hideToast}
          />
        )}
      </div>
    </>
  );
};

// Logout button component
const LogoutButton: React.FC = () => {
  const { logout, user } = useAuth();

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <span style={{ fontSize: '14px', color: '#666' }}>{user?.email}</span>
      <button
        onClick={logout}
        className="secondary-button"
        style={{ padding: '8px 16px', fontSize: '14px' }}
      >
        Logout
      </button>
    </div>
  );
};

// Main app with authentication
const InvoiceApp: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [showRegister, setShowRegister] = useState(false);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return showRegister ? (
      <RegisterForm onSwitchToLogin={() => setShowRegister(false)} />
    ) : (
      <LoginForm onSwitchToRegister={() => setShowRegister(true)} />
    );
  }

  return <App />;
};

// Wrap with AuthProvider
const AppWithAuth: React.FC = () => {
  return (
    <AuthProvider>
      <InvoiceApp />
    </AuthProvider>
  );
};

export default AppWithAuth;
